import { FileDownload } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import settings from '/@/core/settings';
import { useInviteUser } from '/@/hooks/identity';
import useGeneratePDF from '/@/hooks/pdf';
import { _ } from '/@/locale';
import type { VisitReport } from '/@/types/field';
import type { Recommendation } from '/@/types/recommendation';
import { validateEmailV2 } from '/@/utils';
import ModalV2 from '../bayer/modal-v2';
import EmailInputStep from './steps/EmailInputStep';
import EmailSuccessStep from './steps/EmailSuccessStep';
import InitialStepWithNodes from './steps/InitialStepWithNodes';
import InitialStepWithoutNodes from './steps/InitialStepWithoutNodes';
import PdfBlocksStep, { DEFAULT_PDF_BLOCKS, type PdfBlockKey } from './steps/PdfBlocksStep';

/**
 * `generate` opens on the block picker with Enviar/Descargar; `share` opens on it with
 * Siguiente. Both then continue into the existing steps. Anything else skips the picker
 * entirely, which is what keeps visit-report sharing unchanged.
 */
export type ShareItemPopupMode = 'share' | 'generate';

export type ShareItemPopupProps =
    | {
          visible: boolean;
          onClose: () => void;
          itemType: 'recommendation';
          item: Recommendation;
          entrySource?: string;
          /** Which PDF template to ask for. Only version 2 supports block selection. */
          pdfVersion?: number;
          mode?: ShareItemPopupMode;
      }
    | {
          visible: boolean;
          onClose: () => void;
          itemType: 'visit-report';
          item: VisitReport;
          entrySource?: string;
      };

interface ShareItemPopupStep {
    title: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
    dataTestId?: string;
    onClose?: () => void;
    goBack?: () => void;
}

export const ITEM_TYPE_LABELS: Record<
    ShareItemPopupProps['itemType'],
    { singular: string; gender: string; plain: string }
> = {
    recommendation: { singular: _('una recomendación'), gender: _('la recomendación'), plain: _('recomendación') },
    'visit-report': {
        singular: _('un informe de visita'),
        gender: _('el informe de visita'),
        plain: _('informe de visita'),
    },
};

export function ShareItemPopup(props: ShareItemPopupProps) {
    const { visible, onClose, itemType, item, entrySource } = props;
    const pdfVersion = itemType === 'recommendation' ? props.pdfVersion : undefined;
    const mode = itemType === 'recommendation' ? props.mode : undefined;
    // Only the v2 template has selectable sections.
    const picksBlocks = !!mode && pdfVersion === 2;
    const [blocks, setBlocks] = useState<PdfBlockKey[]>(DEFAULT_PDF_BLOCKS);
    // The picker is appended rather than prepended so the existing steps keep their indices —
    // several of them are referenced by number from the step components.
    const blocksStepIndex = 4;
    const firstStep = picksBlocks ? blocksStepIndex : 0;
    const [currentStep, setCurrentStep] = useState<number>(firstStep);

    // The popup stays mounted while closed, and `mode` is only set as it opens — so the entry
    // step has to be chosen on open. `useState`'s initial value is captured at mount, when
    // `mode` is still undefined, which would land the first open on the wrong step.
    useEffect(() => {
        if (visible) setCurrentStep(firstStep);
    }, [visible, firstStep]);
    const [emailInput, setEmailInput] = useState<string | undefined>(undefined);
    const [sharedWith, setSharedWith] = useState<string | undefined>(undefined);
    const [isDownloading, setIsDownloading] = useState(false);

    const { handleEmail, handleWhatsApp, handleDownload } = useGeneratePDF(
        itemType === 'recommendation'
            ? {
                  itemType: 'recommendation',
                  item: item as Recommendation,
                  entrySource,
                  pdfOptions: pdfVersion ? { version: pdfVersion, ...(picksBlocks ? { blocks } : {}) } : undefined,
              }
            : { itemType: 'visit-report', item: item as VisitReport, entrySource }
    );
    const inviteUserMutation = useInviteUser();
    const itemLabel = ITEM_TYPE_LABELS[itemType];

    const shouldShowUserNodes = itemType === 'recommendation';

    const handleClose = () => {
        setCurrentStep(firstStep);
        setEmailInput(undefined);
        setSharedWith(undefined);
        onClose();
    };
    const handleEmailFromInput = async () => {
        if (!emailInput) return;
        await handleEmail?.({ email: emailInput });
        setCurrentStep(2);
    };
    const handleInviteUser = async () => {
        if (!emailInput) return;
        try {
            await inviteUserMutation.trigger({ email: emailInput });
            setCurrentStep(3);
        } catch {
            // Error is handled by the hook
        }
    };
    const steps: ShareItemPopupStep[] = [
        {
            title: !shouldShowUserNodes
                ? _('Compartí {{itemLabel}}', { itemLabel: itemLabel.gender })
                : _('Elegí con quién querés compartir {{itemLabel}}', { itemLabel: itemLabel.gender }),
            children: !shouldShowUserNodes ? (
                <InitialStepWithoutNodes
                    onWhatsAppPDF={handleWhatsApp}
                    setCurrentStep={setCurrentStep}
                    itemType={itemType}
                />
            ) : (
                <InitialStepWithNodes
                    item={itemType === 'recommendation' ? (item as Recommendation) : undefined}
                    onWhatsApp={handleWhatsApp}
                    onEmail={handleEmail}
                    setCurrentStep={setCurrentStep}
                    setSharedWith={setSharedWith}
                />
            ),
            dataTestId: 'share-item-popup',
        },
        {
            title: 'Ingresá un mail',
            children: <EmailInputStep emailInput={emailInput} setEmailInput={setEmailInput} itemType={itemType} />,
            actions: (
                <>
                    <Button variant='text' color='secondary' onClick={() => setCurrentStep(0)}>
                        {_('Cancelar')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={handleEmailFromInput}
                        disabled={!validateEmailV2(emailInput)}
                        data-testid='share-item-popup-email-input-button'
                    >
                        {_('Enviar')}
                    </Button>
                </>
            ),
        },
        {
            title: sharedWith
                ? _('{{itemLabel}} enviada', { itemLabel: capitalize(itemLabel.plain) })
                : _('{{itemLabel}} enviada con éxito', { itemLabel: capitalize(itemLabel.plain) }),
            children: (
                <EmailSuccessStep withInvitation={false} phoneOrEmail={sharedWith} itemLabel={itemLabel.gender} />
            ),
            dataTestId: 'share-item-popup-email-success-step',
            actions: (
                <>
                    <Button variant='text' color='secondary' onClick={() => handleClose()}>
                        {_('Cerrar')}
                    </Button>
                    {!sharedWith && emailInput && (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={handleInviteUser}
                            disabled={!validateEmailV2(emailInput) || inviteUserMutation.isMutating}
                            loading={inviteUserMutation.isMutating}
                        >
                            {_('Invitar a {{appName}}', { appName: settings.app.name })}
                        </Button>
                    )}
                </>
            ),
        },
        {
            title: _('Invitación enviada'),
            children: (
                <Stack>
                    <Typography variant='body1' color='primary'>
                        {_('Le enviamos un correo con el enlace para registrarse en {{appName}}.', {
                            appName: settings.app.name,
                        })}
                    </Typography>
                    <Typography variant='body1' color='primary'>
                        {_('Así podrá acceder a las recomendaciones y empezar a usar la plataforma.')}
                    </Typography>
                </Stack>
            ),
            actions: (
                <>
                    <Button variant='text' color='secondary' onClick={() => handleClose()}>
                        {_('Cerrar')}
                    </Button>
                </>
            ),
        },
        {
            // Index 4: the block picker. Appended, not prepended, so the steps above keep the
            // indices their components already reference by number.
            title: mode === 'generate' ? _('Generar PDF') : _('Compartir la recomendación'),
            children: <PdfBlocksStep selected={blocks} setSelected={setBlocks} />,
            dataTestId: 'share-item-popup-blocks-step',
            actions:
                mode === 'generate' ? (
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => setCurrentStep(0)}
                            disabled={!blocks.length}
                            data-testid='share-item-popup-blocks-send-button'
                        >
                            {_('Enviar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            startIcon={<FileDownload />}
                            disabled={!blocks.length || isDownloading}
                            onClick={async () => {
                                setIsDownloading(true);
                                try {
                                    // `handleDownload` only generates the PDF and hands back
                                    // its link; opening it is what actually delivers the file.
                                    const result = await handleDownload?.();
                                    const url = (result as { file?: { url?: string } } | undefined)?.file?.url;
                                    if (!url) return;
                                    window.open(url);
                                    handleClose();
                                } finally {
                                    setIsDownloading(false);
                                }
                            }}
                            data-testid='share-item-popup-blocks-download-button'
                        >
                            {isDownloading ? _('Generando PDF...') : _('Descargar')}
                        </Button>
                    </>
                ) : (
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => setCurrentStep(0)}
                        disabled={!blocks.length}
                        data-testid='share-item-popup-blocks-next-button'
                    >
                        {_('Siguiente')}
                    </Button>
                ),
        },
    ];

    return (
        <ModalV2
            open={visible}
            title={steps[currentStep]?.title}
            onClose={handleClose}
            actionsProps={{
                justifyContent: 'flex-end',
                alignItems: 'center',
            }}
            actions={steps[currentStep]?.actions}
            data-testid={steps[currentStep]?.dataTestId}
        >
            {steps[currentStep]?.children}
        </ModalV2>
    );
}
